import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  terraProxyApiClient,
  type TargetScopedSignalCountsResult,
} from '../clients/testerClient';
import type {
  AiComponentTypeType,
  BlockedReasonType,
  ConfirmedVulnerabilityFindingStateType,
  SignalCategoryType,
  SignalOriginType,
  SignalStageType,
  SignalStatusType,
  TargetTypeType,
} from '@terra/shared-schemas-orchestrator';
import type { SignalsSearchQueryDto } from '@terra/shared-contracts-signals';
import type { ConfidenceLevelType } from '../types/confidence';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';

export type SignalCountsResult = TargetScopedSignalCountsResult;

export type SignalCountsFilters = {
  stage?: SignalStageType[];
  status?: SignalStatusType[];
  description?: string;
  confidence?: ConfidenceLevelType[];
  category?: SignalCategoryType[];
  origin?: SignalOriginType[];
  source?: SignalsSearchQueryDto['source'];
  url?: string;
  assigneeId?: string[];
  hasExecution?: boolean;
  hasFinding?: boolean;
  hasSecurityTestCase?: boolean;
  isAppWide?: boolean;
  createdStartDate?: Date | null;
  createdEndDate?: Date | null;
  testedStartDate?: Date | null;
  testedEndDate?: Date | null;
  urlPatterns?: string[];
  endpointIds?: string[];
  assetIds?: string[];
  componentTypes?: AiComponentTypeType[];
  rejectedOnly?: boolean;
  confirmedVulnerabilityFindingState?: ConfirmedVulnerabilityFindingStateType[];
  blockedReason?: BlockedReasonType[];
  lifecycleState?: LifecycleStateFilterDto;
};

export const useSignalCounts = ({
  targetId,
  targetType,
  endpointId,
  surfaceAssetEntityId,
  surfaceAssetKind,
  filters,
  includeApplicationLevelGroup,
  enabled = true,
}: {
  targetId?: string;
  targetType?: TargetTypeType;
  endpointId?: string;
  surfaceAssetEntityId?: string;
  surfaceAssetKind?: string;
  filters?: SignalCountsFilters;
  includeApplicationLevelGroup?: boolean;
  enabled?: boolean;
}) => {
  const emptyResult: SignalCountsResult = {
    total: 0,
    counts: {
      ALL: 0,
      HOT: 0,
      COLD: 0,
      PASSED: 0,
      EXPLOITED: 0,
      DUPLICATE: 0,
      BLOCKED: 0,
    },
  };

  const hasEmptyCategoryFilter =
    !filters?.rejectedOnly &&
    filters?.category &&
    Array.isArray(filters.category) &&
    filters.category.length === 0;

  return useQuery({
    queryKey: [
      'signal-counts',
      targetId,
      targetType,
      endpointId,
      surfaceAssetEntityId,
      surfaceAssetKind,
      filters,
      includeApplicationLevelGroup,
    ],
    queryFn: async (): Promise<SignalCountsResult> => {
      if (!targetId || !targetType) {
        return emptyResult;
      }

      if (hasEmptyCategoryFilter) {
        return emptyResult;
      }

      return terraProxyApiClient.countSignalsByTarget({
        targetId,
        targetType,
        endpointId,
        filters,
        includeApplicationLevelGroup,
        surfaceAssetEntityId,
        surfaceAssetKind,
      });
    },
    enabled: !!targetId && !!targetType && enabled,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
  });
};
